import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useQueryClient } from '@tanstack/react-query';
import type {
    OnApproveActions,
    OnApproveData,
    OnCancelledActions,
    PayPalButtonsComponentOptions,
} from '@paypal/paypal-js/types/components/buttons';
import type { PayPalScriptOptions } from '@paypal/paypal-js/types/script-options';
import { type PaymentMethodConfig, PaymentMethodId } from '../../bff-api/baskets/model';
import { useDeletePaymentByMethodId } from '../../bff-api/baskets/useDeletePaymentByMethodId';
import { useDeletePaymentExceptMethodIds } from '../../bff-api/baskets/useDeletePaymentExceptMethodIds';
import { useUpdateBasket } from '../../bff-api/baskets/useUpdateBasket';
import { BffQueryKey } from '../../bff-api/model/BffQueryKey';
import { DEFAULT_CURRENCY } from '../../constants';
import { PageUrl } from '../../pages/utils/page-url';
import { getKioskLocationId } from '../../recaptcha/recaptcha-utils';
import { isHydrated } from '../../utils/utils';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';
import { getJson, postJson } from '../utils';

const URL_GET_PAYPAL_ORDER_TOKEN =
    '/on/demandware.store/Sites-SLT-Site/default/Paypal-StartCheckoutFromCart';
const URL_APPROVE_ORDER = '/on/demandware.store/Sites-SLT-Site/default/Paypal-ReturnFromPaypal';

type PayPalLocation = 'login' | 'billing' | 'cart';

interface IPayPalButtonProps {
    id?: string;
    location: PayPalLocation;
    paypalConfig: PaymentMethodConfig;
    disabled?: boolean;
}

interface ICreatePayPalOrderResponse {
    token?: string;
    error?: string;
}

interface IApprovePayPalOrderResponse {
    redirectUrl: string;
    paypalErrorMessage?: string;
    orderPlaced?: boolean;
    containsPOBox?: boolean;
    dropshipAPO?: boolean;
    isRestrictedPFA?: boolean;
    isSafoniaRestricted?: boolean;
}

const getButtonStyle = (location: PayPalLocation): PayPalButtonsComponentOptions['style'] => {
    const defaultStyle: PayPalButtonsComponentOptions['style'] = {
        color: 'white',
        height: 40,
        disableMaxWidth: true,
    };

    if (location === 'billing') {
        return {
            ...defaultStyle,
            color: 'gold',
            label: 'pay',
        };
    }

    return defaultStyle;
};

const InternalPayPalButton: FC<Pick<IPayPalButtonProps, 'location' | 'disabled'>> = ({
    location,
    disabled,
}) => {
    const [isBeingRedirected, setIsBeingRedirected] = useState(false);
    const deletePaymentByMethodId = useDeletePaymentByMethodId();
    const deletePaymentExceptMethodIds = useDeletePaymentExceptMethodIds();
    const updateBasket = useUpdateBasket();
    const queryClient = useQueryClient();

    const handlePayPalError: (error: string) => void = (error: string) => {
        if (error === 'empty_cart') {
            window.location.reload();
        } else {
            let url = window.location.href;
            url += url.includes('?') ? '&' : '?';
            window.location.href = `${url}showPaypalError=true`;
        }
    };

    const getPayPalOrderToken: () => Promise<string | null> = async () => {
        const getPayPalPalOrderTokenURL = new URL(
            URL_GET_PAYPAL_ORDER_TOKEN,
            window.location.origin,
        );
        getPayPalPalOrderTokenURL.searchParams.append('isFromPWA', 'true');
        getPayPalPalOrderTokenURL.searchParams.append(
            'isFromCart',
            location === 'billing' ? 'false' : 'true',
        );
        getPayPalPalOrderTokenURL.searchParams.append('isAjax', 'true');
        getPayPalPalOrderTokenURL.searchParams.append('pwaStep', location);

        const resp: ICreatePayPalOrderResponse = await postJson(
            getPayPalPalOrderTokenURL.toString(),
            {},
        );
        if (resp.error) {
            handlePayPalError(resp.error);
            console.error(
                `getPayPalOrderToken: ${URL_GET_PAYPAL_ORDER_TOKEN} returned data with error`,
            );
            return null;
        }
        if (!resp.token) {
            console.error('getPayPalOrderToken: data does not have token property');
            return null;
        }
        return resp.token;
    };

    const getApproveOrderCallbackURL: (data: OnApproveData) => string = (data) => {
        const approveOrderURL = new URL(URL_APPROVE_ORDER, window.location.origin);
        approveOrderURL.searchParams.append('isFromPWA', 'true');
        approveOrderURL.searchParams.append(
            'isFromCart',
            location === 'billing' ? 'false' : 'true',
        );
        approveOrderURL.searchParams.append('pwaStep', location);
        approveOrderURL.searchParams.append('token', data.orderID);
        if (data.payerID) {
            approveOrderURL.searchParams.append('payerID', data.payerID);
        }
        const kioskLocationId = getKioskLocationId();
        if (kioskLocationId) {
            approveOrderURL.searchParams.append('kiosk-location-id', kioskLocationId);
        }
        return approveOrderURL.toString();
    };

    const paypalButtonProps: PayPalButtonsComponentOptions = {
        fundingSource: 'paypal',
        style: getButtonStyle(location),

        onApprove: async (data: OnApproveData, actions: OnApproveActions) => {
            setIsBeingRedirected(true);
            const approveOrderURL = getApproveOrderCallbackURL(data);
            const resp: IApprovePayPalOrderResponse = await getJson(approveOrderURL);
            let redirectURL = resp.redirectUrl;

            if (resp.paypalErrorMessage) {
                const newURL = new URL(
                    location === 'cart' ? PageUrl.PWA_CART : PageUrl.CHECKOUT_SHIPPING,
                    window.location.origin,
                );
                newURL.searchParams.append('fromPayPal', 'true');
                if (resp.containsPOBox) {
                    newURL.searchParams.append('containsPOBox', 'true');
                }
                if (resp.dropshipAPO) {
                    newURL.searchParams.append('dropshipAPO', 'true');
                }
                if (resp.isRestrictedPFA) {
                    newURL.searchParams.append('isRestrictedPFA', 'true');
                }
                if (resp.isSafoniaRestricted) {
                    newURL.searchParams.append('isSafoniaRestricted', 'true');
                }
                redirectURL = newURL.toString();
            }

            if (!redirectURL.includes('http')) {
                const newURL = new URL(PageUrl.CHECKOUT_REVIEW, window.location.origin);
                redirectURL = newURL.toString();
            }

            // If there is an error and we are on cart, we stay on cart.
            if (location !== 'cart' || !resp.paypalErrorMessage) {
                actions.redirect(redirectURL);
            } else {
                await queryClient.invalidateQueries([BffQueryKey.BASKET]);
                setIsBeingRedirected(false);
            }
        },
        createOrder: async () => {
            const token = await getPayPalOrderToken();

            if (token) {
                await deletePaymentExceptMethodIds.mutateAsync([
                    PaymentMethodId.PAY_PAL,
                    PaymentMethodId.GIFT_CARD,
                ]);

                return Promise.resolve(token);
            }
            return Promise.reject();
        },
        onCancel: async (data: Record<string, unknown>, actions: OnCancelledActions) => {
            console.debug('PayPal onCancel', data, actions);
            await Promise.all([
                deletePaymentByMethodId.mutateAsync(PaymentMethodId.PAY_PAL),
                updateBasket.mutateAsync({ c_paypalErrorMessage: null }),
            ]);
        },
        onError: async (err: Record<string, unknown>) => {
            console.error('PayPal onError', err);
            await Promise.all([
                deletePaymentByMethodId.mutateAsync(PaymentMethodId.PAY_PAL),
                updateBasket.mutateAsync({ c_paypalErrorMessage: null }),
            ]);
        },
    };

    return (
        <>
            <PayPalButtons {...paypalButtonProps} disabled={disabled} />
            <LoadingIndicator isLoading={isBeingRedirected} />
        </>
    );
};

const PayPalButton: FC<IPayPalButtonProps> = ({ id, paypalConfig, location: step, disabled }) => {
    const [paypalEnabled, setPayPalEnabled] = useState(false);

    useEffect(() => {
        setPayPalEnabled(isHydrated());
    }, []);

    if (!paypalEnabled || !paypalConfig) {
        return null;
    }

    const initialOptions: PayPalScriptOptions = {
        clientId: paypalConfig.clientId ?? '',
        currency: DEFAULT_CURRENCY,
        intent: 'order',
        dataNamespace: 'paypal_sdk',
    };

    return (
        <div className="h-[40px] w-full" data-testid={id}>
            <PayPalScriptProvider options={initialOptions}>
                <InternalPayPalButton location={step} disabled={disabled} />
            </PayPalScriptProvider>
        </div>
    );
};

PayPalButton.defaultProps = {
    id: 'paypal-button',
    disabled: false,
};

export { PayPalButton };
